// postMessage bridge between the portal (inside an iframe) and the partner parent page.
//
//   iframe → parent : { type: 'qtz:ready' }                      on load and whenever a fresh token is needed
//   parent → iframe : { type: 'qtz:auth', accessToken, expiresAt, css?, routes?, theme? }
//   parent → iframe : { type: 'qtz:config', css?, routes?, theme? }
//   parent → iframe : { type: 'qtz:logout' }
//
// Only the access token crosses the boundary. The parent owns refreshing: qtz-account
// rotates refresh tokens, so two holders of the same refresh token would revoke each other.

import { useAuthStore } from '@/stores/authStore'
import { useEmbedStore } from '@/stores/embedStore'
import {
  applyBaseCss,
  applyPartnerCss,
  hideIdsFromSearch,
  isTrustedMessage,
  loadEmbedPolicy,
  parentTarget,
  parseParentMessage,
  sanitizeRoutes,
  sanitizeTheme,
} from '@/lib/embed'

const TOKEN_TIMEOUT_MS = 10_000

let pendingToken: Promise<string> | null = null
let resolveToken: ((token: string) => void) | null = null

function applyConfig(msg: { css?: unknown; routes?: unknown; theme?: unknown }) {
  const embed = useEmbedStore.getState()
  if (typeof msg.css === 'string' && !applyPartnerCss(msg.css)) {
    console.warn('[qtz-embed] partner CSS rejected (too large or invalid)')
  }
  const routes = sanitizeRoutes(msg.routes)
  if (routes) embed.setRoutes(routes.length ? routes : null)
  const theme = sanitizeTheme(msg.theme)
  if (theme) embed.setTheme(theme)
}

function handleMessage(event: MessageEvent) {
  if (!isTrustedMessage(event)) return
  const msg = parseParentMessage(event.data)
  if (!msg) return

  switch (msg.type) {
    case 'qtz:auth':
      try {
        useAuthStore.getState().applyAccessToken(msg.accessToken, msg.expiresAt ?? '')
      } catch {
        console.warn('[qtz-embed] malformed access token')
        return
      }
      applyConfig(msg)
      useEmbedStore.getState().setStatus('ready')
      resolveToken?.(msg.accessToken)
      break
    case 'qtz:config':
      applyConfig(msg)
      break
    case 'qtz:logout':
      useAuthStore.getState().clearSession()
      useEmbedStore.getState().setStatus('failed')
      break
  }
}

// Ask the parent for a (fresh) access token. Concurrent callers share one request.
export function requestTokenFromParent(): Promise<string> {
  const target = parentTarget()
  if (!target) return Promise.reject(new Error('Embedding is not allowed for this origin'))
  // Once the embedded session has ended (qtz:logout, or the parent never answered), a
  // request still in flight that comes back 401 must not ask the parent again — that
  // would sign the frame straight back in. A later qtz:auth from the parent still restores it.
  if (useEmbedStore.getState().status === 'failed') {
    return Promise.reject(new Error('Embedded session has ended'))
  }

  if (!pendingToken) {
    pendingToken = new Promise<string>((resolve, reject) => {
      const timer = setTimeout(() => reject(new Error('No session from embedding page')), TOKEN_TIMEOUT_MS)
      resolveToken = (token) => {
        clearTimeout(timer)
        resolve(token)
      }
      window.parent.postMessage({ type: 'qtz:ready' }, target)
    }).finally(() => {
      pendingToken = null
      resolveToken = null
    })
  }
  return pendingToken
}

export function markEmbedSessionLost() {
  useAuthStore.getState().clearSession()
  useEmbedStore.getState().setStatus('failed')
}

// Call once at startup, only when isEmbedded(). The hide CSS is applied synchronously so it is
// in place at first paint; the handshake waits for the runtime policy (/embed-config.json).
export async function initEmbedBridge() {
  document.documentElement.dataset.qtzEmbedded = ''
  applyBaseCss(hideIdsFromSearch(window.location.search))

  const policy = await loadEmbedPolicy()
  if (!parentTarget()) {
    console.warn(
      policy.mode === 'off'
        ? '[qtz-embed] only same-origin embedding is enabled (set EMBED_ALLOWED_ORIGINS)'
        : '[qtz-embed] embedding origin is not in EMBED_ALLOWED_ORIGINS'
    )
    useEmbedStore.getState().setStatus('failed')
    return
  }

  window.addEventListener('message', handleMessage)
  requestTokenFromParent().catch(() => {
    if (useEmbedStore.getState().status === 'connecting') useEmbedStore.getState().setStatus('failed')
  })
}
